import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Button, Badge, LoadingSkeleton, Modal, ToastContainer } from './ui';
import { PlusIcon, TrashIcon, PencilIcon } from '@heroicons/react/24/outline';
import { safeErrorMessage } from '../utils/apiError';
import { hasAnyRole } from '../utils/roles';
import ClassLevelFields from './ClassLevelFields';

const WRITE_ROLES = ['administrator', 'principal', 'head_of_academics'];

const EMPTY_FORM = { name: '', stream: '', homeroom_teacher_id: '' };

function Classes({ token, user }) {
  const canWrite = hasAnyRole(user, WRITE_ROLES);
  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [toasts, setToasts] = useState([]);

  const addToast = (message, type = 'success') => setToasts(prev => [...prev, { message, type }]);
  const removeToast = (index) => setToasts(prev => prev.filter((_, i) => i !== index));

  const fetchData = useCallback(async () => {
    try {
      const [classesRes, teachersRes] = await Promise.all([
        fetch('/api/v1/classes', { headers: { Authorization: `Bearer ${token}` } }),
        fetch('/api/v1/teachers', { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setClasses(await classesRes.json());
      setTeachers(await teachersRes.json());
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => {
    setEditingClass(null);
    setFormData(EMPTY_FORM);
    setShowForm(true);
  };

  const openEdit = (schoolClass) => {
    setEditingClass(schoolClass);
    setFormData({
      name: schoolClass.name,
      stream: schoolClass.stream || '',
      homeroom_teacher_id: schoolClass.homeroom_teacher_id || '',
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const body = {
      name: formData.name,
      stream: formData.stream.trim() || null,
      homeroom_teacher_id: formData.homeroom_teacher_id || null,
    };
    try {
      const url = editingClass ? `/api/v1/classes/${editingClass.id}` : '/api/v1/classes';
      const response = await fetch(url, {
        method: editingClass ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (response.ok) {
        setShowForm(false);
        fetchData();
      } else {
        addToast(await safeErrorMessage(response, 'Failed to save class'), 'error');
      }
    } catch (error) {
      console.error('Error:', error);
      addToast('Network error. Please check your connection and try again.', 'error');
    }
  };

  const handleDelete = async (schoolClass) => {
    if (!window.confirm(`Deactivate ${schoolClass.display_name}?`)) return;
    try {
      const response = await fetch(`/api/v1/classes/${schoolClass.id}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        fetchData();
      } else if (response.status === 409) {
        const data = await response.json().catch(() => ({}));
        if (window.confirm(`${data.error} — deactivate anyway?`)) {
          const retry = await fetch(`/api/v1/classes/${schoolClass.id}?force=true`, {
            method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
          });
          if (retry.ok) fetchData();
          else addToast(await safeErrorMessage(retry, 'Failed to deactivate class'), 'error');
        }
      } else {
        addToast(await safeErrorMessage(response, 'Failed to deactivate class'), 'error');
      }
    } catch (error) {
      console.error('Error:', error);
      addToast('Network error. Please check your connection and try again.', 'error');
    }
  };

  const columns = [
    { header: 'Class', render: (row) => <strong>{row.display_name}</strong> },
    { header: 'Homeroom Teacher', render: (row) => row.homeroom_teacher_name || '—' },
    { header: 'Status', render: (row) => <Badge variant={row.is_active ? 'success' : 'default'}>{row.is_active ? 'Active' : 'Inactive'}</Badge> },
  ];

  if (canWrite) {
    columns.push({
      header: 'Actions', width: '120px',
      render: (row) => (
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <Button variant="ghost" size="sm" onClick={() => openEdit(row)}>
            <PencilIcon style={{ width: 14, height: 14 }} />
          </Button>
          <Button variant="danger" size="sm" onClick={() => handleDelete(row)}>
            <TrashIcon style={{ width: 14, height: 14 }} />
          </Button>
        </div>
      ),
    });
  }

  if (loading) return <LoadingSkeleton type="text" rows={10} />;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--color-text)' }}>Classes</h2>
        {canWrite && (
          <Button variant="primary" size="sm" onClick={openCreate}>
            <PlusIcon style={{ width: 16, height: 16 }} />
            Add Class
          </Button>
        )}
      </div>

      <Modal isOpen={showForm} onClose={() => setShowForm(false)} title={editingClass ? 'Edit Class' : 'Add Class'}>
        <form onSubmit={handleSubmit}>
          <ClassLevelFields token={token} name={formData.name} stream={formData.stream} onChange={(changes) => setFormData({ ...formData, ...changes })} />
          <div className="form-row">
            <div className="form-group">
              <label>Homeroom Teacher</label>
              <select value={formData.homeroom_teacher_id} onChange={(e) => setFormData({ ...formData, homeroom_teacher_id: e.target.value })}>
                <option value="">None</option>
                {teachers.map(t => <option key={t.id} value={t.id}>{t.full_name}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
            <Button type="submit" variant="primary">{editingClass ? 'Save Changes' : 'Add Class'}</Button>
            <Button type="button" variant="secondary" onClick={() => setShowForm(false)}>Cancel</Button>
          </div>
        </form>
      </Modal>

      <Card title="Classes">
        <Table columns={columns} data={classes} emptyMessage="No classes found." />
      </Card>

      <ToastContainer toasts={toasts} onClose={removeToast} />
    </div>
  );
}

export default Classes;
